import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { fadeInUpAnimation } from 'app/app-layout/animations';
import { EntityType } from 'app/assessment/models';
import { SurveyInterface } from 'app/assessment/survey/models';
import { SurveyService } from 'app/assessment/survey/services/survey.service';
import { UpdateEntity } from 'app/assessment/utilities/update-entity';
import { AuthService } from 'app/core/authentication/services';
import { BrowserService } from 'app/core/browser/services/browser.service';
import { UserInterface } from 'app/core/user/models';
import { DashboardTabIndexEnum } from 'app/my-zone/home/constants/dashboard-tab-index.enum';
import { getDeepCopyOfObject } from 'app/shared/utilities/utility-methods.utility';
import { TaskInterface, TaskTemplateInterface } from 'app/task/models';
import { TaskStatus } from 'app/task/models/task-status.enum';
import { TaskRestConnectorService, TaskService } from 'app/task/services';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { skip, switchMap, take, takeUntil } from 'rxjs/operators';
@Component({
  selector: 'cz-survey',
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss'],
  animations: [fadeInUpAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyComponent implements OnInit, OnDestroy {
  public surveyStarted: boolean = false;
  public surveyInProgress: boolean;
  public surveyComplete: boolean = false;
  public surveyImagePath: string = '/assets/images/components/cz-assessment/';
  public task: TaskInterface;
  public tId: string;

  public currentSurvey$: BehaviorSubject<Readonly<SurveyInterface>>;
  public surveyProgress$: BehaviorSubject<Readonly<number>>;
  public  tasksAssignedToUserId: TaskInterface[];
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly newTaskSubscriptionDestroyer$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _authService: AuthService,
    private readonly _browserService: BrowserService,
    private readonly _surveyService: SurveyService,
    private readonly _taskService: TaskService,
    private readonly _taskConnector: TaskRestConnectorService,
    private _updateSurvey: UpdateEntity,
  ) {
    this.currentSurvey$ = new BehaviorSubject<Readonly<SurveyInterface>>(undefined);
    this.surveyProgress$ = new BehaviorSubject<Readonly<number>>(undefined);
  }

  public ngOnInit(): void {
    this.loadTask();
    this.tId = this._route.snapshot && this._route.snapshot.queryParamMap.get('taskid');
    this._surveyService.getSurveyProgress()
      .pipe(
        takeUntil(this.destroy$))
      .subscribe((progress: number) => {
        this.surveyProgress$.next(progress);
      });

    this._surveyService.getCurrentSurvey(parseInt(this.tId, 10))
      .pipe(
        takeUntil(this.destroy$))
      .subscribe((survey: SurveyInterface) => {
        if (!survey) { return; }

        if (typeof this.currentSurvey$.value === 'undefined' || !this.currentSurvey$.value.date) {
          if (this.tId) {
            // this.surveyInProgress = survey.isLatestSurvey && survey.lastCompletedPage>0;
            if(survey.isLatestSurvey){
              this.surveyInProgress = survey.lastCompletedPage>0;
            } else {
              this.surveyInProgress = false;
            }            
          } else {
            // this.surveyInProgress = false;
            if(survey.isLatestSurvey){
              this.surveyInProgress = survey.lastCompletedPage>0;
            } else {
              this.surveyInProgress = false;
            }
          }

          this.currentSurvey$.next(survey);
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public loadTask(): void {
    const taskId: string = this._route.snapshot && this._route.snapshot.queryParamMap.get('taskid');
    if (!taskId || taskId === '') {
      combineLatest(
        this._taskService.getTaskTemplates(),
        this._authService.getAuthenticatedUser(),
      ).pipe(
        takeUntil(this.newTaskSubscriptionDestroyer$),
      ).subscribe((data: [TaskTemplateInterface[], UserInterface]) => {
        if(!data[0] || !data[1]) { return; }

        const taskUser: UserInterface = data[1] as UserInterface;
        const templates: TaskTemplateInterface[] = data[0] as TaskTemplateInterface[];
        const templateToUse: TaskTemplateInterface = templates.filter((template: TaskTemplateInterface) => {
          if(template.entityType === EntityType.SURVEY) {
            return template;
          }
        })[0];

        const newTask: TaskInterface = {
          assignedTo: taskUser,
          subject: taskUser,
          dueDate: templateToUse.defaultDueDate,
          hours: templateToUse.defaultHours,
          notes: templateToUse.defaultNotes,
          summary: templateToUse.defaultSummary,
          taskTemplateName: templateToUse.templateName,
          taskType: templateToUse.id,
          taskTypeLabel: templateToUse.taskName,
          isVisibleToSubject: true,
        };

        this._taskService.createSurveyTask(newTask)
        .pipe(takeUntil(this.newTaskSubscriptionDestroyer$))
        .subscribe((surveyTask: TaskInterface) => {
          if(!surveyTask) { return; }

          this.task = surveyTask;

          this.newTaskSubscriptionDestroyer$.next(true);
          this.newTaskSubscriptionDestroyer$.complete();
        });
      });
      return;
    }

    this._authService.getAuthenticatedUser().pipe(
      takeUntil(this.destroy$),
      switchMap((authenticatedUser: UserInterface) => {
        if (authenticatedUser === undefined) {
          return of(undefined);
        }

        return this._taskService.loadAll().pipe(takeUntil(this.destroy$));
      }),
      switchMap((tasks: { [id: number ]: TaskInterface }) => {
        if (tasks === undefined) {
          return of(undefined);
        }

        return this._taskService.findOne(parseInt(taskId, 10)).pipe(take(1));
      }))
      .subscribe((task: TaskInterface) => {
        if (!task) {
          return;
        }
        this.task = task;
      });
  }

  public continueToRecommendations(): void {
    this._browserService.toDashboard(DashboardTabIndexEnum.recommendations);
  }

  public endSurvey(): void {
    this.surveyComplete = true;
    this.surveyStarted = false;
  }

  public startSurvey(): void {
    if (this.task === undefined) {
      return;
    }
    this.surveyStarted = true;
    this._taskConnector.loadTasksAssignedToUser(this.task.subject.id).pipe(
      takeUntil(this.destroy$))
      .subscribe((tasks: TaskInterface[]) => {
        this.tasksAssignedToUserId = tasks.filter((aTask: TaskInterface) => {
          return aTask.taskTemplateName === EntityType.SURVEY && !aTask.isComplete && aTask.assignedTo;

        });
        const maxTid: number = Math.max.apply(Math, this.tasksAssignedToUserId.map((item: TaskInterface) => { return item.id; }));

        if (this.task.status !== TaskStatus.COMPLETE) {
          const updatedTask: TaskInterface = getDeepCopyOfObject(this.task);
          if (this.task.id >= maxTid) {
            updatedTask.status = TaskStatus.IN_PROGRESS;
          }

          this._taskService.update(updatedTask);
          this.task = updatedTask;
        }

      });
  }

  public startNewSurvey(): void {
    const updatedSurvey: SurveyInterface = this._updateSurvey.resetEntity(this.currentSurvey$.value);
    this._surveyService.resetCurrentSurvey(updatedSurvey).pipe(
      skip(1),
      take(1),
    ).subscribe((survey: SurveyInterface) => {
      this.currentSurvey$.next(survey);
      this.startSurvey();
    });
  }
}
